/**
 * Direct Messages — list view. Not tied to any client. Shows every thread
 * the current user can see, newest activity first, with a red pill when
 * a reply is needed.
 *
 * Auto-refreshes every 60s (matches Unified Inbox cadence).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageSquarePlus, ChevronRight, Circle } from "lucide-react";
import { Card } from "../components/ui/card";
import { listDirectThreads } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import RepToInternalComposeModal from "../components/RepToInternalComposeModal";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

const RECIPIENT_LABEL = {
  rep: "→ Rep",
  manager: "→ Manager",
  agency_broadcast: "→ All reps",
  internal: "→ Ready Prospect",
};

export default function DirectMessagesPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  // External partners (reps + managers of partner agencies) can only direct-
  // message the Ready Prospect team. Internal staff start DMs from the
  // Agency/Rep detail pages, where they can pick a specific recipient.
  const isPartner = user?.role === "rep" || user?.role === "manager";

  const load = async () => {
    try {
      const { data } = await listDirectThreads();
      setThreads(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load direct threads", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  const replyNeeded = threads.filter((t) => t.reply_needed);
  const others = threads.filter((t) => !t.reply_needed);

  return (
    <div className="space-y-6" data-testid="direct-messages-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold">Direct Messages</h1>
          <p className="text-sm text-muted-foreground">
            {isPartner
              ? "Send a message to the Ready Prospect team — not tied to a specific client."
              : "Send and receive general messages with reseller partners — not tied to a specific client."}
          </p>
        </div>
        {isPartner && <RepToInternalComposeModal />}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : threads.length === 0 ? (
        <Card className="p-8 text-center" data-testid="direct-messages-empty">
          <MessageSquarePlus className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-3">
            {isPartner
              ? "No direct messages yet. Use the New message button above to ping the Ready Prospect team."
              : "No direct messages yet. Start one from any Agency or Rep page."}
          </p>
          {isPartner && (
            <div className="flex justify-center">
              <RepToInternalComposeModal />
            </div>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {replyNeeded.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase text-red-700">Reply needed ({replyNeeded.length})</div>
              {replyNeeded.map((t) => <ThreadRow key={t.id} thread={t} />)}
            </div>
          )}
          {others.length > 0 && (
            <div className="space-y-2">
              {replyNeeded.length > 0 && (
                <div className="text-xs font-semibold uppercase text-zinc-500">Everything else</div>
              )}
              {others.map((t) => <ThreadRow key={t.id} thread={t} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThreadRow({ thread }) {
  return (
    <Link
      to={`/direct/${thread.id}`}
      className="block"
      data-testid={`direct-thread-row-${thread.id}`}
    >
      <Card className={`px-4 py-3 hover:border-primary/40 transition ${thread.reply_needed ? "border-red-200 bg-red-50/40" : ""}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {thread.reply_needed && (
                <Circle className="h-2 w-2 fill-red-500 text-red-500" />
              )}
              <span className="font-medium truncate">{thread.subject || "(no subject)"}</span>
              <span className="text-[10px] text-muted-foreground font-mono uppercase">
                {RECIPIENT_LABEL[thread.recipient_type] || thread.recipient_type}
              </span>
              {thread.is_handled && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                  Handled
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              From {thread.created_by_name || "—"} · Last activity {timeAgo(thread.last_activity_at)}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
      </Card>
    </Link>
  );
}
